"use client";

import { useRef, useEffect, useState } from "react";
import { Magnetic } from "@/components/ui/Magnetic";
import type { DomainId } from "@/data/domains";
import type { SpinTransition } from "@/hooks/useMetaRing";

// =============================================================================
// CORNER NAV
// =============================================================================
// Shows adjacent domain labels in top corners for meta ring navigation.
// Top-left: left domain, Top-right: right domain
// Labels animate with arcing motion during ring spins.

interface CornerNavProps {
  activeDomain: DomainId;
  leftDomain: DomainId;
  rightDomain: DomainId;
  isSpinning: boolean;
  spinTransition: SpinTransition | null;
  onSpinTo: (domain: DomainId) => void;
  getDomainLabel: (domain: DomainId) => string;
  visibility?: number;
  crtEnabled?: boolean;
}

// Animation durations
const ARC_OUT_DURATION = 350;
const ARC_IN_DURATION = 400;
const ARC_IN_DELAY = 100;
const CROSSFADE_DURATION = 200;

// Inject keyframes into document (once)
const KEYFRAMES_ID = "corner-nav-keyframes";
function injectKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;

  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes arcOutLeft {
      0% { transform: translate(0, 0) scale(1); opacity: 0.5; }
      100% { transform: translate(8vw, 6vh) scale(1.1); opacity: 0; }
    }
    @keyframes arcOutRight {
      0% { transform: translate(0, 0) scale(1); opacity: 0.5; }
      100% { transform: translate(-8vw, 6vh) scale(1.1); opacity: 0; }
    }
    @keyframes arcInFromRight {
      0% { transform: translate(6vw, 3vh) scale(1.05); opacity: 0; }
      100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
    }
    @keyframes arcInFromLeft {
      0% { transform: translate(-6vw, 3vh) scale(1.05); opacity: 0; }
      100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
    }
    @keyframes fadeOut {
      0% { opacity: 0.5; }
      100% { opacity: 0; }
    }
    @keyframes fadeIn {
      0% { opacity: 0; }
      100% { opacity: 0.5; }
    }
  `;
  document.head.appendChild(style);
}

export function CornerNav({
  activeDomain,
  leftDomain,
  rightDomain,
  isSpinning,
  spinTransition,
  onSpinTo,
  getDomainLabel,
  visibility = 1,
  crtEnabled = true,
}: CornerNavProps) {
  // Inject keyframes on mount
  useEffect(() => {
    injectKeyframes();
  }, []);

  // Track animation phase
  const [isAnimating, setIsAnimating] = useState(false);
  const [showExiting, setShowExiting] = useState(false);
  const [showEntering, setShowEntering] = useState(false);

  // Snapshot of labels when animation starts
  const [exitingLeft, setExitingLeft] = useState<DomainId | null>(null);
  const [exitingRight, setExitingRight] = useState<DomainId | null>(null);

  // Track which corner was clicked and spin direction
  const [clickedCorner, setClickedCorner] = useState<"left" | "right" | null>(null);
  const [spinDirection, setSpinDirection] = useState<"cw" | "ccw" | null>(null);

  // Prevent re-triggering animation
  const hasAnimatedRef = useRef(false);

  // Handle spin start - trigger animations
  useEffect(() => {
    if (isSpinning && spinTransition && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;

      // Snapshot current labels
      setExitingLeft(spinTransition.prevLeftLabel);
      setExitingRight(spinTransition.prevRightLabel);
      setClickedCorner(spinTransition.clickedCorner);
      setSpinDirection(spinTransition.direction);

      // Start animations
      setIsAnimating(true);
      setShowExiting(true);

      // Enter animation starts with slight delay
      const enterTimer = setTimeout(() => {
        setShowEntering(true);
      }, ARC_IN_DELAY);

      // Hide exiting after animation
      const exitTimer = setTimeout(() => {
        setShowExiting(false);
      }, ARC_OUT_DURATION + 50);

      // Complete all animations - use fixed duration, don't wait for spring
      const completeTimer = setTimeout(() => {
        setIsAnimating(false);
        setShowEntering(false);
        setExitingLeft(null);
        setExitingRight(null);
        setClickedCorner(null);
        setSpinDirection(null);
        hasAnimatedRef.current = false;
      }, Math.max(ARC_OUT_DURATION, ARC_IN_DELAY + ARC_IN_DURATION) + 100);

      return () => {
        clearTimeout(enterTimer);
        clearTimeout(exitTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isSpinning, spinTransition]);

  // Safety: reset animation state when spinning stops (in case timers didn't fire)
  useEffect(() => {
    if (!isSpinning && isAnimating) {
      // Give a small delay to let any pending animations complete
      const safetyTimer = setTimeout(() => {
        setIsAnimating(false);
        setShowExiting(false);
        setShowEntering(false);
        hasAnimatedRef.current = false;
      }, 100);
      return () => clearTimeout(safetyTimer);
    }
  }, [isSpinning, isAnimating]);

  if (visibility <= 0) return null;

  const baseLeftOpacity = Math.max(0, (visibility - 0.2) / 0.8);
  const baseRightOpacity = Math.max(0, (visibility - 0.4) / 0.6);

  const buttonClass = "font-mono text-sm font-bold tracking-widest uppercase pointer-events-auto cursor-pointer bg-transparent border-none opacity-50 hover:opacity-100 transition-opacity disabled:cursor-wait px-4 py-3 -mx-4 -my-3";
  const buttonStyle: React.CSSProperties = {
    color: "rgba(var(--foreground-rgb), 1)",
    textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
    transform: crtEnabled ? "perspective(200px) rotateX(20deg)" : "none",
    transformOrigin: "bottom center",
  };

  // Get animation name for a corner/mode combination
  const getAnimation = (
    corner: "left" | "right",
    mode: "exiting" | "entering"
  ): string => {
    const isClickedCorner = corner === clickedCorner;

    if (mode === "exiting") {
      if (isClickedCorner) {
        // Arc toward center
        return corner === "left"
          ? `arcOutLeft ${ARC_OUT_DURATION}ms cubic-bezier(0.4, 0, 0.8, 1) forwards`
          : `arcOutRight ${ARC_OUT_DURATION}ms cubic-bezier(0.4, 0, 0.8, 1) forwards`;
      } else {
        // Simple fade
        return `fadeOut ${CROSSFADE_DURATION}ms ease-out forwards`;
      }
    } else {
      // Entering
      if (isClickedCorner) {
        // Arc in from direction of spin
        // CW = ring moves "right to left", so new label comes from right
        // CCW = ring moves "left to right", so new label comes from left
        if (spinDirection === "cw") {
          return `arcInFromRight ${ARC_IN_DURATION}ms cubic-bezier(0.2, 0, 0.2, 1) forwards`;
        } else {
          return `arcInFromLeft ${ARC_IN_DURATION}ms cubic-bezier(0.2, 0, 0.2, 1) forwards`;
        }
      } else {
        // Simple fade
        return `fadeIn ${CROSSFADE_DURATION}ms ease-in forwards`;
      }
    }
  };

  const renderLabel = (
    corner: "left" | "right",
    domain: DomainId,
    mode: "static" | "exiting" | "entering"
  ) => {
    const isLeft = corner === "left";
    const baseOpacity = isLeft ? baseLeftOpacity : baseRightOpacity;
    const positionClass = isLeft ? "absolute top-4 left-4" : "absolute top-4 right-4";

    let style: React.CSSProperties = {};

    if (mode === "static") {
      style = {
        opacity: baseOpacity,
        transform: crtEnabled
          ? `perspective(400px) rotateX(10deg) translateY(${(1 - baseOpacity) * 10}px)`
          : `translateY(${(1 - baseOpacity) * 10}px)`,
        transformOrigin: "bottom center",
        textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
        transition: "opacity 0.3s ease, transform 0.3s ease",
      };
    } else {
      style = {
        animation: getAnimation(corner, mode as "exiting" | "entering"),
        pointerEvents: "none",
      };
    }

    // Only disable during CSS animations (non-static modes)
    const isDisabled = mode !== "static";

    return (
      <div key={`${corner}-${mode}-${domain}`} className={positionClass} style={style}>
        <Magnetic strength={0.2} tiltStrength={8} padding={80} disabled={isDisabled}>
          <button
            onClick={() => !isDisabled && !isAnimating && onSpinTo(isLeft ? leftDomain : rightDomain)}
            disabled={isDisabled || isAnimating}
            className={buttonClass}
            style={buttonStyle}
          >
            {getDomainLabel(domain)}
          </button>
        </Magnetic>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[250] pointer-events-none" data-corner-nav>
      {/* Left corner */}
      {!isAnimating && renderLabel("left", leftDomain, "static")}
      {isAnimating && showExiting && exitingLeft && renderLabel("left", exitingLeft, "exiting")}
      {isAnimating && showEntering && renderLabel("left", leftDomain, "entering")}

      {/* Right corner */}
      {!isAnimating && renderLabel("right", rightDomain, "static")}
      {isAnimating && showExiting && exitingRight && renderLabel("right", exitingRight, "exiting")}
      {isAnimating && showEntering && renderLabel("right", rightDomain, "entering")}
    </div>
  );
}
